import { useRef, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Section, Container, SectionTitle } from '../../ui';
import { SearchIcon, TruckIcon, DocumentIcon, ShieldIcon, PuzzleIcon, CheckIcon } from '../../icons';
import styles from './ServiceBlocks.module.css';

const icons = [SearchIcon, TruckIcon, DocumentIcon, ShieldIcon, PuzzleIcon];

const illustrations = [
  { src: '/images/catalogue-platform.webp', alt: 'Interface catalogue QBP Solutions avec filtres et stocks', screenshot: true },
  { src: '/images/expedition-logistique.webp', alt: 'Livraison colis par transporteur' },
  { src: '/images/facturation-b2b.webp', alt: 'Facturation B2B et gestion documentaire' },
  { src: '/images/sav-retours.webp', alt: 'Technicien réparant un smartphone en atelier' },
  { src: '/images/integration-api.webp', alt: 'Code source d\'une intégration API' },
] as const;

interface BlockItem {
  title: string;
  badge: string;
  text: string;
  highlights: string[];
}

export function ServiceBlocks() {
  const { t } = useTranslation('solutions');
  const ref = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);
  const hasAnimated = useRef(false);

  const blocks = t('serviceBlocks.items', { returnObjects: true }) as BlockItem[];

  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasAnimated.current) {
          hasAnimated.current = true;
          setIsVisible(true);
        }
      },
      { threshold: 0.05 }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  return (
    <Section variant="light">
      <Container>
        <SectionTitle
          title={t('serviceBlocks.title')}
          subtitle={t('serviceBlocks.subtitle')}
          centered
        />
        <div ref={ref} className={`${styles.rows} ${isVisible ? styles.visible : ''}`}>
          {blocks.map((block, i) => {
            const Icon = icons[i];
            const illus = illustrations[i];
            const reversed = i % 2 !== 0;
            return (
              <div
                key={i}
                className={`${styles.row} ${reversed ? styles.reversed : ''}`}
                style={{ '--row-index': i } as React.CSSProperties}
              >
                <div className={styles.textSide}>
                  <span className={styles.badge}>{block.badge}</span>
                  <div className={styles.titleGroup}>
                    <div className={styles.iconWrap}>
                      <Icon size={20} color="var(--cyan-technique)" />
                    </div>
                    <h3 className={styles.rowTitle}>{block.title}</h3>
                  </div>
                  <p className={styles.rowText}>{block.text}</p>
                  <ul className={styles.highlights}>
                    {block.highlights.map((h, j) => (
                      <li key={j} className={styles.highlightItem}>
                        <CheckIcon size={16} color="var(--cyan-technique)" />
                        <span>{h}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div className={styles.illustrationSide}>
                  <div className={'screenshot' in illus ? styles.screenshotWrap : styles.photoWrap}>
                    <img
                      src={illus.src}
                      alt={illus.alt}
                      className={'screenshot' in illus ? styles.screenshot : styles.photo}
                      loading="lazy"
                    />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </Container>
    </Section>
  );
}
